<template>
  <div class="mx-auto mt-8 max-w-2xl px-4 lg:max-w-7xl lg:px-8">
      <div class="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
        <div class="lg:col-span-5 lg:col-start-8">
          <div class="flex justify-between">
            <h1 class="text-xl font-medium text-gray-900">{{ product.name }}</h1>
            <p class="text-xl font-medium text-gray-900">{{ price }}</p>
          </div>
        </div>

        <!-- Image gallery -->
        <div class="mt-8 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0 lg:self-start flex flex-col-reverse">
          <h2 class="sr-only">Images</h2>

          <!-- Image selector -->
          <div class="mx-auto mt-6 w-full max-w-2xl sm:block lg:max-w-none">
            <div class="grid grid-cols-4 gap-6" aria-orientation="horizontal" role="tablist">
              <button v-for="photo in photos" id="tabs-1-tab-1" class="relative flex h-24 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium uppercase text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-opacity-50 focus:ring-offset-4" aria-controls="tabs-1-panel-1" role="tab" type="button">
                <span class="absolute inset-0 overflow-hidden rounded-md">
                  <img :src="photo.url" alt="Product photo" class="h-full w-full object-cover object-center" @click="activePhoto = photo">
                </span>
                <!-- Selected: "ring-green-500", Not Selected: "ring-transparent" -->
                <span class="pointer-events-none absolute inset-0 rounded-md ring-2 ring-offset-2"
                      :class="[photo === activePhoto ? 'ring-blue-200' : 'ring-transparent']"
                      aria-hidden="true"></span>
              </button>
              <!-- More images... -->
            </div>
          </div>

          <div class="aspect-h-1 aspect-w-1 w-full">
            <img :src="activePhoto.url" alt="Product photo" class="h-full w-full object-cover object-center sm:rounded-lg">
          </div>
        </div>
        <div class="mt-8 lg:col-span-5">
          <form>
            <!-- for the clothign case, we have a dynamic style color sku picker functionality -->
            <div v-if="availableSizes.length > 0 || availableColors.length > 0">
              <!-- Colors picker -->
              <div class="" v-if="availableColors.length > 0">
                <h2 class="text-sm font-medium text-gray-900">Color</h2>
                <fieldset class="mt-2">
                  <legend class="sr-only">Choose a color</legend>
                  <!--
                    In Stock: "cursor-pointer", Out of Stock: "opacity-25 cursor-not-allowed"
                    Active: "ring-2 ring-green-500 ring-offset-2"
                    Checked: "border-transparent bg-green-600 text-white hover:bg-green-700",
                    Not Checked: "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                  -->
                  <div class="flex flex-wrap gap-3">
                    <label v-for="(color,index) in availableColors"
                           :key="index"
                           class="flex items-center justify-center rounded-md border py-3 px-3 text-sm font-medium uppercase sm:flex-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 hover:opacity-90"
                           :class="[color === activeColor ? 'border-transparent' : 'border-gray-200']"
                           :style="[color === activeColor ? selectedBtnStyles : btnStyles]"
                           @click="activeColor = color"
                    >
                      <input type="radio" name="color-choice" :value="color" class="sr-only" :aria-labelledby="`color-choice-${index}-label`"  v-on:click="activeColor = color" :checked="color === activeColor">
                      <span :id="`color-choice-${index}-label`">{{ color }}</span>
                    </label>
                  </div>
                </fieldset>
              </div >
              <!-- Size picker -->
              <div class="mt-8" v-if="availableSizes.length > 0">
                <div class="flex items-center justify-between">
                  <h2 class="text-sm font-medium text-gray-900">Size</h2>
                </div>
                <fieldset class="mt-2">
                  <legend class="sr-only">Choose a size</legend>
                  <!--
                    In Stock: "cursor-pointer", Out of Stock: "opacity-25 cursor-not-allowed"
                    Active: "ring-2 ring-green-500 ring-offset-2"
                    Checked: "border-transparent bg-green-600 text-white hover:bg-green-700",
                    Not Checked: "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                  -->
                  <div class="grid grid-cols-3 gap-3 sm:grid-cols-6">
                    <label v-for="(size,index) in availableSizes"
                           :key="index"
                           class="flex items-center justify-center rounded-md border py-3 px-3 text-sm font-medium uppercase sm:flex-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 hover:opacity-90"
                           :class="[size === activeSize ? 'border-transparent' : 'border-gray-200']"
                           :style="[size === activeSize ? selectedBtnStyles : btnStyles]"
                           @click="activeSize = size"
                    >
                      <input type="radio" name="size-choice" :value="size" class="sr-only" :aria-labelledby="`size-choice-${index}-label`"  v-on:click="activeSize = size" :checked="size === activeSize">
                      <span :id="`size-choice-${index}-label`">{{ size }}</span>
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>
            <!-- for everything else, we just have a generic sku picker if needed -->
            <div v-else-if="availableBillingPlans.length > 1">
              <div class="flex items-center justify-between">
                <h2 class="text-sm font-medium text-gray-900">Options</h2>
              </div>
              <fieldset class="mt-2">
                <legend class="sr-only">Choose an option</legend>
                <!--
                  In Stock: "cursor-pointer", Out of Stock: "opacity-25 cursor-not-allowed"
                  Active: "ring-2 ring-green-500 ring-offset-2"
                  Checked: "border-transparent bg-green-600 text-white hover:bg-green-700",
                  Not Checked: "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                -->
                <div class="grid grid-cols-1 gap-3">
                  <label v-for="(plan,index) in availableBillingPlans"
                         :key="index"
                         class="flex items-center justify-center rounded-md border py-3 px-3 text-sm font-medium uppercase sm:flex-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 hover:opacity-90"
                         :class="[plan === selectedBillingPlan ? 'border-transparent' : 'border-gray-200']"
                         :style="[plan === selectedBillingPlan ? selectedBtnStyles : btnStyles]"
                         @click="selectedBillingPlan = plan"
                  >
                    <input type="radio" name="plan-choice" :value="plan.id" class="sr-only" :aria-labelledby="`plan-${index}-label`"  v-on:click="selectedBillingPlan = plan" :checked="selectedBillingPlan === plan">
                    <span :id="`plan-${index}-label`">{{ plan.name }} - {{ formatAsCurrency(plan.amount) }}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            <!-- Upsells -->
            <div class="mt-8" v-if="availableUpsells.length > 0">
              <div class="flex items-center justify-between">
                <h2 class="text-sm font-medium text-gray-900">Upgrade options</h2>
              </div>
              <div class="space-y-4 mt-2">
                <div class="w-full relative" v-for="(upsell,index) in availableUpsells" :key="index">
                  <div class="flex items-start">
                    <div class="flex h-6 items-center">
                      <input type="checkbox" :name="`upsell-choice-${index}`" :aria-describedby="`upsell-choice-description-${index}`"
                             :style="checkboxStyles"
                             class="h-4 w-4 rounded border-gray-400 bg-white focus:ring-blue-600"
                             v-bind:checked="upsellSelected(upsell)"
                             @change="toggleUpsell(upsell)">
                    </div>
                    <div class="ml-3 text-sm leading-6 flex-1">
                      <div class="flex justify-between">
                        <label :for="`upsell-choice-${index}`" class="font-medium text-gray-700"> {{ upsell.name }}</label>
                        <p class="font-medium text-gray-700">+{{ upsellPrice(upsell) }}</p>
                      </div>
                      <p :id="`upsell-choice-description-${index}`" class="text-gray-500">{{ upsell.description }}</p>
                      <div class="mt-2" v-if="upsellSelected(upsell) && upsell.require_user_input">
                        <label :for="`upsell-input-${index}`" class="block text-sm font-medium text-gray-700">{{ upsell.input_label }}</label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                          <input type="text"
                                 :name="upsell.input_label"
                                 :id="`upsell-input-${index}`"
                                 :value="upsellInputs[upsell.id]" @input="handleUpsellInput(upsell, $event.target.value)"
                                 class="shadow-sm block w-full border-gray-300 rounded-md sm:text-sm"
                                 required
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit"
                    @click.prevent="addToCart"
                    :disabled="addingToCart || !selectedBillingPlan || disableAddToCart"
                    class="mt-8 flex w-full items-center justify-center space-x-2 rounded-md border border-transparent px-8 py-3 text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 hover:border-white disabled:opacity-50"
                    :style="selectedBtnStyles"
            >
              <span v-if="disableAddToCart">
                Store closed
              </span>
              <span v-else>
                Add to cart<span v-if="this.price !== ''">&nbsp;-&nbsp;{{ this.price }}</span>
              </span>
              <span v-if="addingToCart">
                <wrestler-icon stroke-color="white"></wrestler-icon>
              </span>
            </button>
            <p v-if="error" class="text-red-500 text-xs text-center py-2">{{ errorMessage }}</p>

            <div class="rounded-md p-4" v-if="showAddedMsg">
              <div class="flex items-center justify-center">
                <div class="flex-shrink-0">
                  <svg class="h-5 w-5" style="color: #27AA83" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium" style="color: #27AA83">Added to cart!</p>
                </div>
              </div>
            </div>
          </form>

          <!-- Product details -->
          <div class="mt-10">
            <h2 class="text-sm font-medium text-gray-900">Description</h2>
            <div class="prose prose-sm mt-4 text-gray-600" v-html="product.description">
            </div>
          </div>
          <!-- Fabric & Care -->
          <div v-if="product.fabric_and_care" class="mt-10 border-t border-gray-200 pt-8">
            <h2 class="text-sm font-medium text-gray-900">Fabric &amp; Care</h2>
            <div class="prose prose-sm mt-4 text-gray-600 trix-content">
              <div v-html="product.fabric_and_care"></div>
            </div>
          </div>
          <!-- Attachments -->
          <div class="mt-10" v-if="product.attachments.length > 0">
            <h2 class="text-sm font-medium text-gray-900">More Info</h2>
            <div class="prose prose-sm mt-4 text-gray-500">
              <ul>
                <li v-for="attachment in product.attachments">
                  <a :href="attachment.url" class="underline text-gray-600 font-normal" target="_blank">{{ attachment.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>
<script>
import { currencyMix } from '../shared/currency_mix';
import WrestlerIcon from './wrestler-icon.vue';
export default {
  name: 'product',
  components: { WrestlerIcon },
  mixins: [currencyMix],
  props: {
    product: {
      type: Object,
      required: true
    },
    addToCartUrl: {
      type: String,
      required: true
    },
    brandColor: {
      type: String,
      default: '#27AA83',
    },
    brandTextColor: {
      type: String,
      default: '#FFF'
    },
    disableAddToCart: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      // Used for browsing experience interactions
      selectedBillingPlan: null,
      // The upsells checked
      selectedUpsells: [],
      // The user inputed data, keyed by id of upsell
      // TODO is this really the best way? surely not
      upsellInputs: {},
      photos: null,
      activePhoto: null,
      activeSize: null,
      activeColor: null,

      // Button styles
      selectedBtnStyles: {
        color: this.brandTextColor,
        backgroundColor: this.brandColor,
      },
      btnStyles: {
        color: '#18181B', // text-gray-900
        backgroundColor: 'white',
      },
      checkboxStyles: {
        accentColor: this.brandColor, // accentColor is broken due to tailwind forms plugin. For now we hard code color if tick box is not white.
        color: this.brandTextColor === '#FFF' ? this.brandColor : '#2563eb',
      },

      // Add to cart stuff
      addingToCart: false,
      error: false,
      errorMessage: false,
      showAddedMsg: false,
    }
  },
  created() {
    this.photos = _.uniqBy(_.flatMap(this.product.billing_plans, 'photos'), 'url');
    this.activePhoto = _.head(this.photos) || { url: 'https://mktg-cdn.wrestlingiq.com/placeholders/placeholder.png' };
    this.setupInitialSizeColor();
  },
  watch: {
    activeSize() {
      this.findAndSetPlan();
    },
    activeColor() {
      this.findAndSetPlan()
    }
  },
  methods: {
    setupInitialSizeColor() {
      // Don't make user pick if we only have one option
      if (this.availableSizes.length === 1) {
        this.activeSize = _.head(this.availableSizes);
      } else {
        this.activeSize = null;
      }

      if (this.availableColors.length === 1) {
        this.activeColor = _.head(this.availableColors);
      } else {
        this.activeColor = null;
      }

      // If we get hear we could be in a situation where it's a single product, single sku, no size or colors. So force a set of the plan
      if (this.availableBillingPlans.length === 1) {
        this.findAndSetPlan();
      }
    },
    findAndSetPlan() {
      if (this.availableBillingPlans.length === 1) {
        this.selectedBillingPlan = _.head(this.availableBillingPlans);
        return;
      }

      if (this.activeSize == null || this.activeColor == null) {
        this.selectedBillingPlan = null;
        return;
      }
      this.selectedBillingPlan = _.head(_.filter(this.availableBillingPlans, { 'size': this.activeSize, 'color': this.activeColor }));
      let vm = this;
      this.selectedUpsells = _.filter(this.selectedUpsells, function(u) { return _.find(vm.selectedBillingPlan.upsells, [{ 'id': u.id, 'active': true }]) });
    },
    upsellPayload() {
      let upsells = []
      let vm = this;
      this.selectedUpsells.forEach((u) => {
          upsells.push({
            id: u.id,
            user_input: vm.upsellInputs[u.id],
          })
      });

      return upsells;
    },
    addToCart() {
      const vm = this;

      let upsellError = false;
      let upsellErorrMsg = ""
      this.selectedUpsells.forEach((u) => {
        if (u.require_user_input && vm.upsellInputs[u.id] === "") {
          upsellError = true;
          upsellErorrMsg = `Please fill out info for the ${u.name} upgrade.`;
        }
      });

      if (upsellError) {
        this.errorMessage = upsellErorrMsg;
        this.error = true;
        return;
      }

      this.addingToCart = true;
      this.error = false;

      const params = {
        billing_plan_id: this.selectedBillingPlan.id,
        upsells: this.upsellPayload(),
      }
      const headers = {
        "X-CSRF-Token": document.querySelector('meta[name=csrf-token]').content
      }

      axios.post(this.addToCartUrl, params, { headers })
          .then((response) => {
            vm.addingToCart = false;
            vm.showAddedMsg = true;
            setTimeout(function () {
              vm.showAddedMsg = false;
            }, 1500);
            vm.$notificationManager.$emit('cart-count-changed', response.data.cart_count);
            vm.selectedUpsells = []
            vm.upsellInputs = {}
            this.setupInitialSizeColor();
          })
          .catch((error) => {
            vm.addingToCart = false;
            if (axios.isCancel(error)) {
              console.log('request cancelled');
            } else {
              vm.errorMessage = `Error adding to cart ${error.toString()}`;
              vm.error = true;
            }
          });
    },
    upsellSelected(upsell) {
      return _.filter(this.selectedUpsells, ['id', upsell.id]).length > 0;
    },
    toggleUpsell(upsell) {
      if (this.upsellSelected(upsell)) {
        // Remove this upsell
        this.selectedUpsells = _.filter(this.selectedUpsells, function(u) { return u.id !== upsell.id });
      } else {
        this.selectedUpsells.push(upsell)
        // Key in the user input data, as needed.
        if (!this.upsellInputs[upsell.id]) {
          this.$set(this.upsellInputs, upsell.id, "");
        }
      }
    },
    handleUpsellInput(upsell, text) {
      this.$set(this.upsellInputs, upsell.id, text);
    },
    upsellPrice(upsell) {
      return this.formatAsCurrency(upsell.amount, 2);
    }
  },
  computed: {
    price() {
      if (!this.selectedBillingPlan) {
        return ""
      }

      let total = this.selectedBillingPlan.amount
      this.selectedUpsells.forEach((u) => {
        total += u.amount
      });
      return this.formatAsCurrency(total, 2);
    },
    availableSizes() {
      const sizeMap = {
        'YXXXS': 1,
        'Y3XS': 2,
        'Y2XS': 3,
        'YXXS': 4,
        'YXS': 5,
        'YS': 6,
        'YM': 7,
        'YL': 8,
        'YXL': 9,
        'XXXS': 10,
        '3XS': 11,
        '2XS': 12,
        'XXS': 13,
        'XS': 14,
        'S': 15,
        'SM': 16,
        'M': 17,
        'L': 18,
        'LG': 19,
        'XL': 20,
        'XXL': 21,
        '2XL': 22,
        '3XL': 23,
        '4XL': 24,
        'BXXXS': 25,
        'B3XS': 26,
        'B2XS': 27,
        'BXXS': 28,
        'BXS': 29,
        'BS': 30,
        'BM': 31,
        'BL': 32,
        'BXL': 33,
        'MXXXS': 34,
        'M3XS': 35,
        'M2XS': 36,
        'MXXS': 37,
        'MXS': 38,
        'MS': 39,
        'MM': 40,
        'ML': 41,
        'MXL': 42,
        'M2XL': 43,
        'M3XL': 44,
        'M4XL': 45,
        'WXXXS': 46,
        'W3XS': 47,
        'W2XS': 48,
        'WXXS': 49,
        'WXS': 50,
        'WS': 51,
        'WM': 52,
        'WL': 53,
        'WXL': 54,
        'W2XL': 55,
        'W3XL': 56,
        'W4XL': 57,
        'AXXXS': 58,
        'A3XS': 59,
        'A2XS': 60,
        'AXXS': 61,
        'AXS': 62,
        'AS': 63,
        'AM': 64,
        'AL': 65,
        'AXL': 66,
        'A2XL': 67,
        'AXXL': 68,
        'A3XL': 69,
        'A4XL': 70,
      }

      // Chagne the function below to ignore nulls with lodash

      return _.sortBy(_.uniq(_.compact(_.flatMap(this.availableBillingPlans, 'size'))), function(size) {
        return sizeMap[size.toUpperCase()] || 100;
      });
    },
    availableColors() {
      return _.uniq(_.compact(_.flatMap(this.availableBillingPlans, 'color')));
    },
    availableUpsells() {
      if (!this.selectedBillingPlan) {
        return [];
      }

      // return all upsells that have active truthy
      return _.filter(this.selectedBillingPlan.upsells, ['active', true]);
    },
    availableBillingPlans() {
      return _.filter(this.product.billing_plans, ['active', true]);
    }
  }
};
</script>
